import React from 'react';
import { ShieldAlertIcon, ClockIcon, DollarSignIcon, AlertTriangleIcon, TrendingUpIcon } from 'lucide-react';
import Card from '../../components/ui/Card';
import Table from '../../components/ui/Table';
import StatusBadge from '../../components/ui/StatusBadge';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend } from 'recharts';
import { useNavigate } from 'react-router-dom';
const DisputeDashboard: React.FC = () => {
  const navigate = useNavigate();
  // Mock Data
  const kpis = [{
    title: 'Open Disputes',
    value: '142',
    change: '+12%',
    icon: ShieldAlertIcon,
    color: 'blue'
  }, {
    title: 'SLA Breached',
    value: '18',
    change: '-5%',
    icon: ClockIcon,
    color: 'red'
  }, {
    title: 'Financial Exposure',
    value: 'PKR 4.2M',
    change: '+2.4%',
    icon: DollarSignIcon,
    color: 'orange'
  }, {
    title: 'Fraud Claims',
    value: '45',
    change: '+18%',
    icon: AlertTriangleIcon,
    color: 'purple'
  }];
  const disputeTypeData = [{
    name: 'Refund',
    value: 45,
    color: '#3b82f6'
  }, {
    name: 'Retrieval',
    value: 30,
    color: '#10b981'
  }, {
    name: 'Fraud',
    value: 25,
    color: '#ef4444'
  }];
  const monthlyTrendData = [{
    month: 'Jan',
    disputes: 120,
    chargebacks: 40
  }, {
    month: 'Feb',
    disputes: 150,
    chargebacks: 55
  }, {
    month: 'Mar',
    disputes: 180,
    chargebacks: 65
  }, {
    month: 'Apr',
    disputes: 140,
    chargebacks: 45
  }, {
    month: 'May',
    disputes: 190,
    chargebacks: 70
  }, {
    month: 'Jun',
    disputes: 142,
    chargebacks: 50
  }];
  const recentDisputes = [{
    id: 'DSP-2023-001',
    merchant: 'TechStore PK',
    type: 'Fraud',
    amount: 45000,
    status: 'Under Review',
    sla: '2 days left'
  }, {
    id: 'DSP-2023-002',
    merchant: 'Fashion Hub',
    type: 'Refund',
    amount: 12500,
    status: 'Evidence Submitted',
    sla: '5 days left'
  }, {
    id: 'DSP-2023-003',
    merchant: 'Grocery Plus',
    type: 'Retrieval',
    amount: 3400,
    status: 'Merchant Notified',
    sla: '1 day left'
  }, {
    id: 'DSP-2023-004',
    merchant: 'Electro World',
    type: 'Fraud',
    amount: 89000,
    status: 'Created',
    sla: '7 days left'
  }, {
    id: 'DSP-2023-005',
    merchant: 'Travel Agency',
    type: 'Refund',
    amount: 150000,
    status: 'SLA Breached',
    sla: 'Overdue'
  }];
  const columns = [{
    header: 'Dispute ID',
    accessor: 'id',
    className: 'font-medium text-blue-600 dark:text-blue-400'
  }, {
    header: 'Merchant',
    accessor: 'merchant'
  }, {
    header: 'Type',
    accessor: 'type'
  }, {
    header: 'Amount',
    accessor: (row: any) => `PKR ${row.amount.toLocaleString()}`
  }, {
    header: 'Status',
    accessor: (row: any) => {
      let statusType: any = 'default';
      if (row.status === 'Under Review') statusType = 'warning';
      if (row.status === 'Evidence Submitted') statusType = 'info';
      if (row.status === 'SLA Breached') statusType = 'danger';
      return <StatusBadge status={statusType} text={row.status} data-id="element-3745" />;
    }
  }, {
    header: 'SLA',
    accessor: (row: any) => <span className={row.sla === 'Overdue' ? 'text-red-600 font-semibold' : 'text-gray-600 dark:text-gray-400'} data-id="element-3746">
          {row.sla}
        </span>
  }];
  return <div className="space-y-6" data-id="element-3747">
      <div data-id="element-3748">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-3749">
          Dispute Management Dashboard
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-3750">
          Overview of card transaction disputes, retrieval requests, and
          financial exposure.
        </p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-3751">
        {kpis.map((kpi, index) => {
        const Icon = kpi.icon;
        return <Card key={index} className="bg-white dark:bg-slate-800" data-id="element-3752">
              <div className="flex items-center justify-between" data-id="element-3753">
                <div data-id="element-3754">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-3755">
                    {kpi.title}
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1" data-id="element-3756">
                    {kpi.value}
                  </p>
                </div>
                <div className={`p-3 rounded-full bg-${kpi.color}-100 dark:bg-${kpi.color}-900/30 text-${kpi.color}-600 dark:text-${kpi.color}-400`} data-id="element-3757">
                  <Icon className="h-6 w-6" data-id="element-3758" />
                </div>
              </div>
              <div className="mt-4 flex items-center text-sm" data-id="element-3759">
                <TrendingUpIcon className={`h-4 w-4 mr-1 ${kpi.change.startsWith('+') ? 'text-red-500' : 'text-green-500'}`} data-id="element-3760" />
                <span className={kpi.change.startsWith('+') ? 'text-red-500' : 'text-green-500'} data-id="element-3761">
                  {kpi.change}
                </span>
                <span className="text-gray-500 ml-2" data-id="element-3762">vs last month</span>
              </div>
            </Card>;
      })}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-3763">
        <Card title="Disputes by Type" className="lg:col-span-1" data-id="element-3764">
          <div className="h-64" data-id="element-3765">
            <ResponsiveContainer width="100%" height="100%" data-id="element-3766">
              <PieChart data-id="element-3767">
                <Pie data={disputeTypeData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value" data-id="element-3768">
                  {disputeTypeData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-3769" />)}
                </Pie>
                <Tooltip data-id="element-3770" />
                <Legend data-id="element-3771" />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card title="Dispute & Chargeback Trends" className="lg:col-span-2" data-id="element-3772">
          <div className="h-64" data-id="element-3773">
            <ResponsiveContainer width="100%" height="100%" data-id="element-3774">
              <BarChart data={monthlyTrendData} margin={{
              top: 10,
              right: 30,
              left: 0,
              bottom: 0
            }} data-id="element-3775">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#374151" opacity={0.2} data-id="element-3776" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} data-id="element-3777" />
                <YAxis axisLine={false} tickLine={false} data-id="element-3778" />
                <Tooltip cursor={{
                fill: 'transparent'
              }} data-id="element-3779" />
                <Legend data-id="element-3780" />
                <Bar dataKey="disputes" name="Total Disputes" fill="#3b82f6" radius={[4, 4, 0, 0]} data-id="element-3781" />
                <Bar dataKey="chargebacks" name="Chargebacks" fill="#ef4444" radius={[4, 4, 0, 0]} data-id="element-3782" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Recent Disputes Table */}
      <Card title="Action Required: Recent Disputes" data-id="element-3783">
        <Table columns={columns} data={recentDisputes} keyField="id" pagination={false} onRowClick={row => navigate(`/disputes/${row.id}`)} data-id="element-3784" />
      </Card>
    </div>;
};
export default DisputeDashboard;